import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {firstValueFrom, Observable, of} from 'rxjs';
import {catchError, timeout} from 'rxjs/operators';
import PortfolioAccountStore from 'src/app/domain/store/portfolio-account.store';
import PortfolioSnapshotStore from 'src/app/domain/store/portfolio-snapshot.store';
import {ApiClient} from 'src/app/infrastructure/http/api.client';
import {Modal} from 'src/app/infrastructure/service/modal.service';
import {TrackService} from 'src/app/infrastructure/service/track.service';
import MenuStatusStore from 'src/app/infrastructure/store/menuStatusStore';
import ResumeSlideStore from 'src/app/infrastructure/store/resume-slide.store';
import {Login, LoginWithAuthCode, Recover, Register} from 'src/app/domain/command/auth.command';
import {objKeysToSnakeCase, objToCamelCase} from 'src/app/domain/function/object.helper';
import {Auth, AuthGrantType} from 'src/app/domain/model/auth.model';
import AuthStore from 'src/app/domain/store/auth.store';
import AuthHttpInterceptorStateStore from 'src/app/domain/store/auth-http-interceptor-state.store';
import CustomerPortfolioStore from 'src/app/domain/store/customer-portfolio.store';
import UserStore from 'src/app/domain/store/user.store';
import UserApi from 'src/app/domain/service/api/user.api';
import PortfolioSnapshotSummaryApi from 'src/app/domain/service/api/portfolio-snapshot-summary.api';
import CustomerApi from 'src/app/domain/service/api/customer/customer.api';
import CustomerPortfolioApi from 'src/app/domain/service/api/customer-portfolio.api';
import PortfolioAccountApi from 'src/app/domain/service/api/portfolio-account.api';
import UserPromotionApi from 'src/app/domain/service/api/user/user-promotion.api';
import CustomerStore from 'src/app/domain/store/customer/customer.store';
import UserPromotionStore from 'src/app/domain/store/user/user-promotion.store';
import MyJointHolderInvitationStore from 'src/app/domain/store/portfolio/my-joint-holder-invitation.store';
import JointHolderInvitationsExitStore from 'src/app/infrastructure/store/joint-holder-invitations-exit.store';
import JointHolderInvitationsToProcessStore from 'src/app/domain/store/portfolio/joint-holder-invitations-to-process.store';

@Injectable({
  providedIn: 'root'
})
export default class AuthApi {
  private scheduleRefreshTokenInterval = -1;

  constructor(
    private api: ApiClient,
    private authStore: AuthStore,
    private userStore: UserStore,
    private customerPortfolioStore: CustomerPortfolioStore,
    private customerStore: CustomerStore,
    private snapshotStore: PortfolioSnapshotStore,
    private snapshotSummaryApi: PortfolioSnapshotSummaryApi,
    private customerApi: CustomerApi,
    private customerPortfolioApi: CustomerPortfolioApi,
    private userApi: UserApi,
    private menuStatusStore: MenuStatusStore,
    private resumeSlideStore: ResumeSlideStore,
    private accountStore: PortfolioAccountStore,
    private accountApi: PortfolioAccountApi,
    private promotionStore: UserPromotionStore,
    private promotionApi: UserPromotionApi,
    private trackService: TrackService,
    private modal: Modal,
    private http: HttpClient,
    private authHttpInterceptorStore: AuthHttpInterceptorStateStore,
    private myJointHolderInvitationStore: MyJointHolderInvitationStore,
    private jointHolderInvitationsExitStore: JointHolderInvitationsExitStore,
    private jointHolderInvitationsToProcessStore: JointHolderInvitationsToProcessStore
  ) {
  }

  async recover(command: Recover) {
    await this.api.post('login/recover', command);
  }

  public me(): Observable<Auth> {
    return this.authStore.me$;
  }

  public async register(command: Register): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);

    const clientIp = await this.getClientIP();

    if (clientIp) {
      (snakeCommand as any).ip_client = clientIp;
    }

    const auth: any = await this.api.post('register', snakeCommand);

    this.resetSessionStores();
    this.authStore.saveMe(auth);
    this.authHttpInterceptorStore.reset();
    await this.userApi.fetchMe();

    this.scheduleRefreshToken(auth.expiresIn);
    await this.trackService.trackRegister(command);
  }

  public async login(command: Login, addClientIP: boolean): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);

    if (addClientIP) {
      const clientIp = await this.getClientIP();

      if (clientIp) {
        (snakeCommand as any).ip_client = clientIp;
      }
    }

    const response: any = await this.api.post('login', snakeCommand);
    const auth: Auth = objToCamelCase(response);

    this.resetSessionStores();
    this.authStore.saveMe(auth);
    this.authHttpInterceptorStore.reset();
    this.jointHolderInvitationsExitStore.reset();
    this.jointHolderInvitationsToProcessStore.reset();

    const user = await this.userApi.waitFetchAndSync();
    this.scheduleRefreshToken(auth.expiresIn);
    await this.trackService.trackLogin(command.username);

    /* eslint-disable @typescript-eslint/naming-convention */
    const data = {
      first_name: user.userData.name,
      surname: user.userData.firstSurname,
      secondSurname: user.userData.secondSurname,
      email: user.email,
      username: user.username,
      created_at: user.registeredAt
    };

    await this.trackService.identifyUser(user.id, data);
  }

  public async loginWithAuthCode(command: LoginWithAuthCode): Promise<void> {
    const snakeCommand: object = objKeysToSnakeCase(command);
    const clientIp = await this.getClientIP();

    if (clientIp) {
      (snakeCommand as any).ip_client = clientIp;
    }
    const response: any = await this.api.post('login/recover/confirm', snakeCommand);
    const auth: Auth = objToCamelCase(response);

    this.resetSessionStores();
    this.authStore.saveMe(auth);
    this.authHttpInterceptorStore.reset();
    await this.userApi.fetchMe();

    this.scheduleRefreshToken(auth.expiresIn);
  }

  public async refreshToken(): Promise<void> {
    const refreshToken = this.authStore.syncMe()?.refreshToken;
    const data: any = {};

    if (!refreshToken) {
      throw new Error('Error Refresh Token');
    }

    data.refresh_token = refreshToken;
    data.grant_type = AuthGrantType.refreshToken;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const params = {grant_type: AuthGrantType.refreshToken};

    try {
      const auth: Auth = await this.api.post('login', data, params);

      this.authStore.saveMe(auth);
      this.scheduleRefreshToken(auth.expiresIn);

    } catch (e) {
      this.clearRefreshTokenInterval();
      throw new Error('Error Refresh Token');
    }
  }

  public logout() {
    this.modal.dismiss();
    this.authStore.reset();
    this.resetSessionStores();
    this.clearRefreshTokenInterval();
  }

  private resetSessionStores(): void {
    this.snapshotStore.resetSession();
    this.snapshotSummaryApi.invalidate();
    this.userStore.reset();
    this.customerApi.invalidate();
    this.customerStore.reset();
    this.customerPortfolioApi.invalidate();
    this.customerPortfolioStore.reset();
    this.resumeSlideStore.reset();
    this.menuStatusStore.reset();
    this.accountStore.reset();
    this.myJointHolderInvitationStore.reset();
    this.jointHolderInvitationsExitStore.reset();
    this.jointHolderInvitationsToProcessStore.reset();
    this.accountApi.invalidate();
    this.promotionStore.reset();
    this.promotionApi.invalidate();
  }

  private scheduleRefreshToken(expiresInSeconds: number): void {
    const expiresInMillis = Math.round(expiresInSeconds * 1000 * 0.8);

    if (this.scheduleRefreshTokenInterval !== -1) {
      return;
    }

    this.scheduleRefreshTokenInterval = window.setInterval(async () => {
      await this.refreshToken();
    }, expiresInMillis);
  }

  private clearRefreshTokenInterval() {
    clearInterval(this.scheduleRefreshTokenInterval);
  }

  private async getClientIP(): Promise<string|null> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ ip: string }>('https://api.ipify.org?format=json').pipe(
          timeout(5000),
          catchError(() => of(null))
        )
      );
      return response?.ip ?? null;
    } catch (error) {
      return null;
    }
  }
}
